import { getSpaceName } from "@app/lib/spaces";
import type { SpaceCategoryInfo } from "@app/types/api/spaces";
import type { SpaceType } from "@app/types/space";
import {
  Button,
  ContentMessage,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Spinner,
  Trash01,
} from "@dust-tt/sparkle";
import { useTranslation } from "react-i18next";

interface ConfirmDeleteSpaceDialogProps {
  space: SpaceType;
  handleDelete: () => void;
  spaceInfoByCategory: { [key: string]: SpaceCategoryInfo } | undefined;
  isDeleting: boolean;
}

export function ConfirmDeleteSpaceDialog({
  space,
  handleDelete,
  spaceInfoByCategory,
  isDeleting,
}: ConfirmDeleteSpaceDialogProps) {
  const { t } = useTranslation();
  const uniqueAgentNames = spaceInfoByCategory
    ? [
        ...new Set(
          Object.values(spaceInfoByCategory)
            .flatMap((category) => category.usage.agents)
            .map((agent) => agent.name)
            .filter((name) => name && name.length > 0)
        ),
      ]
    : [];

  const spaceName = `${getSpaceName(space)}`;
  const hasAgents = uniqueAgentNames.length > 0;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="flex w-full flex-col items-end">
          <Button
            icon={Trash01}
            size="xs"
            variant="warning"
            label={t("spaces.deleteSpaceButton", "Delete space")}
          />
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("spaces.deletingSpaceTitle", "Deleting {{spaceName}}", {
              spaceName: getSpaceName(space),
            })}
          </DialogTitle>
        </DialogHeader>
        {isDeleting ? (
          <div className="flex justify-center py-8">
            <Spinner variant="dark" size="md" />
          </div>
        ) : (
          <>
            <DialogContainer className="space-y-4">
              {hasAgents && (
                <ContentMessage
                  variant="warning"
                  // TODO: change to show names of public agents and then number of unpublished agents
                  title={t("common.spaceDeletionImpactsAgents", {
                    defaultValue:
                      "{{count}} agents use tools that depend on this space and will be impacted by its deletion",
                    count: uniqueAgentNames.length,
                  })}
                />
              )}
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("common.deleteSpaceConfirm", {
                    defaultValue:
                      "Are you sure you want to permanently delete space {{spaceName}}? This action cannot be undone.",
                    spaceName,
                  })}
                </p>
              </div>
            </DialogContainer>
            <DialogFooter
              leftButtonProps={{
                label: t("spaces.deleteSpaceCancelButton", "Cancel"),
                variant: "outline",
              }}
              rightButtonProps={{
                label: t("spaces.deleteSpaceDeleteButton", "Delete"),
                variant: "warning",
                onClick: async () => {
                  void handleDelete();
                },
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
